import React, { useState, useRef } from "react";
import { session } from "../shared/api/session";
import { BRAND_NAME, LOGO_URL } from "../shared/ui/brand";
import { colors, FONTS } from "../shared/ui/tokens";
import { useMediaQuery } from "../shared/ui/useBreakpoint";
import { PAGE_META } from "./useTabs";

export type Page =
  | "accueil"
  | "dashboard"
  | "attribution_role" // ← conservé tel quel (clé interne / route), seul le libellé affiché change en "Users"
  | "mdx"
  | "ai_query"
  | "ia_famille"
  | "ia_sous_famille" // ← AJOUTER CETTE LIGNE
  | "articles"
  | "magazin"
  | "marques"
  | "ventes"
  | "sousfamille"
  | "saison"
  | "groupe"
  | "univers"
  | "taille"
  | "couleur"
  | "couleur-taille"
  | "famille"
  | "vendeur"
  | "prevision_f" // ← AJOUTER CETTE LIGNE
  | "profile";

interface AppBarProps {
  page: Page;
  setPage: (p: Page) => void;
  onLogout?: () => void;
  userRole?: string; // ✅ rôle de l'utilisateur connecté (Admin Général, Vendeur, Responsable Boutique...)
}

// ── AVDASH brand colours ──────────────────────────────────────────────────────
const NW_BRAND = {
  bgDeep: colors.brand[700],
  bgMid: colors.brand[600],
  bgLight: colors.brand[500],
  mauve: colors.accent[300],
  mauveGlow: colors.brand[600],
  mauveDim: "rgba(127,200,189,0.18)",
  mauveBorder: "rgba(127,200,189,0.25)",
  white: colors.neutral[0],
  dimWhite: "rgba(255,255,255,0.80)",
  faintLine: "rgba(255,255,255,0.07)",
};

// ── Logo ─────────────────────────────────────────────────────────────────────
const ClientLogo: React.FC = () => (
  <div
    style={{
      width: 38,
      height: 38,
      borderRadius: 10,
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      // Pastille blanche : le logo (bleu, transparent) resterait invisible sur la barre bleue
      background: "#FFFFFF",
      padding: 3,
      boxSizing: "border-box",
      boxShadow: `0 0 14px ${NW_BRAND.mauveDim}, 0 0 0 1px ${NW_BRAND.mauveBorder}`,
    }}
  >
    <img src={LOGO_URL} alt={BRAND_NAME} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
  </div>
);
// ── Normalise une chaîne (minuscules + sans accents) pour comparaison fiable ──
const normalizeRole = (r?: string) =>
  (r || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const isAdminGeneral = (role?: string) =>
  normalizeRole(role).includes("admin");
// ── Analyse dropdown sub-items ────────────────────────────────────────────────
const ANALYSE_ITEMS: {
  key: Page;
  label: string;
  icon: string;
}[] = [
  {
    key: "magazin",
    label: "Analyse Magazins",
    icon: PAGE_META["magazin"].icon,
  },
  {
    key: "marques",
    label: "Analyse Marques",
    icon: PAGE_META["marques"].icon,
  },
  {
    key: "ventes",
    label: "Analyse Groupes Article",
    icon: PAGE_META["ventes"].icon,
  },
  {
    key: "sousfamille",
    label: "Analyse Sous Famille",
    icon: PAGE_META["sousfamille"].icon,
  },
  {
    key: "saison",
    label: "Analyse Saison",
    icon: PAGE_META["saison"].icon,
  },
  {
    key: "groupe",
    label: "Analyse Groupe",
    icon: PAGE_META["groupe"].icon,
  },
  {
    key: "univers",
    label: "Analyse Univers",
    icon: PAGE_META["univers"].icon,
  },
  {
    key: "taille",
    label: "Analyse Taille",
    icon: PAGE_META["taille"].icon,
  },
  {
    key: "couleur",
    label: "Analyse Couleur",
    icon: PAGE_META["couleur"].icon,
  },
  {
    key: "couleur-taille",
    label: "Analyse Couleur Taille",
    icon: PAGE_META["couleur-taille"].icon,
  },
  {
    key: "famille",
    label: "Analyse Famille",
    icon: PAGE_META["famille"].icon,
  },
  {
    key: "vendeur",
    label: "Analyse Vendeurs",
    icon: PAGE_META["vendeur"].icon,
  },
];

// ── IA dropdown sub-items ─────────────────────────────────────────────────────
const IA_ITEMS: {
  key: Page;
  label: string;
  icon: string;
}[] = [
  {
    key: "ai_query",
    label: "Query Builder",
    icon: PAGE_META["ai_query"].icon,
  },
  {
    key: "ia_famille",
    label: "Famille",
    icon: PAGE_META["ia_famille"].icon,
  },
  {
    key: "ia_sous_famille",
    label: "Sous-Famille",
    icon: PAGE_META["ia_sous_famille"].icon,
  },
];

// ── Top-level tabs (Accueil, Dashboard, MDX, Prévision F) ────────────────────
const TOP_TABS: { key: Page; label: string; icon: string }[] = [
  { key: "accueil", label: "Accueil", icon: PAGE_META["accueil"].icon },
  { key: "dashboard", label: "Top N Dashboard", icon: PAGE_META["dashboard"].icon },
  { key: "mdx", label: "MDX Query Builder", icon: PAGE_META["mdx"].icon },
  { key: "prevision_f", label: "Prévision F", icon: PAGE_META["prevision_f"].icon },
  { key: "attribution_role", label: "Users", icon: PAGE_META["attribution_role"].icon },
];

// ── Menu de navigation des petits écrans (panneau latéral clair) ──────────────
interface NavItem {
  key: Page;
  label: string;
  icon: string;
}

const MobileNav: React.FC<{
  page: Page;
  sections: { title: string; items: NavItem[] }[];
  onSelect: (p: Page) => void;
  onClose: () => void;
}> = ({ page, sections, onSelect, onClose }) => (
  <>
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: "62px 0 0 0",
        background: "rgba(15,23,42,0.30)",
        zIndex: 119,
      }}
    />
    <nav
      aria-label="Navigation principale"
      style={{
        position: "fixed",
        top: 62,
        left: 0,
        bottom: 0,
        width: "min(320px, 86vw)",
        background: colors.neutral[0],
        borderRight: `1px solid ${colors.neutral[200]}`,
        boxShadow: "8px 0 24px rgba(15,23,42,0.12)",
        overflowY: "auto",
        padding: "12px 10px 24px",
        zIndex: 120,
        fontFamily: FONTS.body,
      }}
    >
      {sections.map((section) => (
        <div key={section.title} style={{ marginBottom: 14 }}>
          <div
            style={{
              padding: "8px 12px 6px",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: colors.neutral[500],
            }}
          >
            {section.title}
          </div>
          {section.items.map((item) => {
            const active = item.key === page;
            return (
              <button
                key={item.key}
                onClick={() => onSelect(item.key)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  border: "none",
                  borderRadius: 8,
                  background: active ? colors.brand[50] : "transparent",
                  color: active ? colors.brand[700] : colors.neutral[700],
                  fontFamily: FONTS.body,
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span style={{ width: 20, textAlign: "center" }}>{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  </>
);

const AppBar: React.FC<AppBarProps> = ({
  page,
  setPage,
  onLogout,
  userRole,
}) => {
  const [analyseOpen, setAnalyseOpen] = useState(false);

  // ── Responsive : nav compacte, puis menu ☰ quand elle ne tient plus ──────
  const navCompact = useMediaQuery("(max-width: 1439px)");
  const navCollapsed = useMediaQuery("(max-width: 1199px)");
  const [menuOpen, setMenuOpen] = useState(false);
  const goTo = (p: Page) => {
    setMenuOpen(false);
    setPage(p);
  };

  // ✅ "Analyse Magazins" visible uniquement pour Admin Général
  const isVendeur = normalizeRole(userRole) === "vendeur";

  const visibleAnalyseItems = isAdminGeneral(userRole)
    ? ANALYSE_ITEMS
    : isVendeur
      ? ANALYSE_ITEMS.filter(
          (item) => item.key !== "magazin" && item.key !== "vendeur",
        )
      : ANALYSE_ITEMS.filter((item) => item.key !== "magazin");
  const analyseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [iaOpen, setIaOpen] = useState(false);
  const iaTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Settings dropdown state ───────────────────────────────────────
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAnalysePage = [
    "magazin",
    "marques",
    "ventes",
    "sousfamille",
    "saison",
    "groupe",
    "univers",
    "taille",
    "couleur",
    "couleur-taille",
    "famille",
    "vendeur",
  ].includes(page);

  const isIaPage = ["ia_famille", "ia_sous_famille", "ai_query"].includes(
    page,
  );

  const openDropdown = () => {
    if (analyseTimeout.current) clearTimeout(analyseTimeout.current);
    setAnalyseOpen(true);
  };
  const closeDropdown = () => {
    analyseTimeout.current = setTimeout(() => setAnalyseOpen(false), 120);
  };

  const openIaDropdown = () => {
    if (iaTimeout.current) clearTimeout(iaTimeout.current);
    setIaOpen(true);
  };
  const closeIaDropdown = () => {
    iaTimeout.current = setTimeout(() => setIaOpen(false), 120);
  };

  // ── Settings dropdown handlers ────────────────────────────────────
  const openSettings = () => {
    if (settingsTimeout.current) clearTimeout(settingsTimeout.current);
    setSettingsOpen(true);
  };
  const closeSettings = () => {
    settingsTimeout.current = setTimeout(() => setSettingsOpen(false), 150);
  };

  // ── Get user info from localStorage ──────────────────────────────
  const getUserName = (): string => {
    try {
      const user = session.getUser<Record<string, any>>();
      if (!user) return "Utilisateur";
      return (
        user?.fullName || user?.username || user?.Username || "Utilisateur"
      );
    } catch {
      return "Utilisateur";
    }
  };

  // ── Get profile photo URL from localStorage ───────────────────────
  const getPhotoUrl = (): string => {
    try {
      const user = session.getUser<Record<string, any>>();
      if (!user) return "";
      // Try photoUrl in bi_user first
      if (user?.photoUrl || user?.PhotoUrl)
        return user.photoUrl || user.PhotoUrl;
      // Fallback: dedicated key bi_photo_{username}
      const id =
        user?.username ||
        user?.Username ||
        user?.email ||
        user?.Email ||
        "default";
      return localStorage.getItem(`bi_photo_${id}`) ?? "";
    } catch {
      return "";
    }
  };

  const handleLogout = () => {
    setSettingsOpen(false);
    if (onLogout) onLogout();
  };

  const handleGoProfile = () => {
    setSettingsOpen(false);
    setPage("profile");
  };

  // ── Top-level tabs visibles selon le rôle ────────────────────────────────
  // ✅ "Users" (ex Attribution Role) visible UNIQUEMENT pour Admin Général.
  //    Responsable Boutique et Vendeur ne voient pas cet onglet du tout.
  const visibleTopTabs = isAdminGeneral(userRole)
    ? TOP_TABS
    : TOP_TABS.filter((t) => t.key !== "attribution_role");

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        background: `linear-gradient(135deg, ${NW_BRAND.bgDeep} 0%, ${NW_BRAND.bgMid} 55%, #2077BC 100%)`,
        boxShadow: `0 2px 24px rgba(24,75,140,0.45), 0 1px 0 ${NW_BRAND.mauveBorder}`,
        display: "flex",
        alignItems: "stretch",
        height: 62,
        width: "100%",
      }}
    >
      {/* Decorative top-border glow */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${NW_BRAND.mauve}60, ${NW_BRAND.mauve}AA, ${NW_BRAND.mauve}60, transparent)`,
          pointerEvents: "none",
        }}
      />

      {/* ── Logo block ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 11,
          padding: navCompact ? "0 14px" : "0 22px",
          borderRight: `1px solid ${NW_BRAND.faintLine}`,
          minWidth: navCompact ? 0 : 210,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            flexShrink: 0,
            filter: `drop-shadow(0 0 10px ${NW_BRAND.mauveDim})`,
          }}
        >
          <ClientLogo />
        </div>

        <div>
          <div
            style={{
              fontFamily: "Poppins, Inter, system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 16,
              color: NW_BRAND.white,
              letterSpacing: "-0.4px",
              whiteSpace: "nowrap",
            }}
          >
            AvDash
          </div>
          {!navCompact && (
            <div
              style={{
                fontSize: 9,
                color: NW_BRAND.dimWhite,
                fontWeight: 700,
                letterSpacing: 2.2,
                marginTop: -1,
                textTransform: "uppercase",
              }}
            >
              Intelligence Platform
            </div>
          )}
        </div>
      </div>

      {/* ── Nav tabs ── */}
      <nav
        style={{
          display: navCollapsed ? "none" : "flex",
          alignItems: "stretch",
          flex: 1,
          minWidth: 0,
          padding: navCompact ? "0 4px" : "0 10px",
        }}
      >
        {/* ── Top-level tabs ── */}
        {visibleTopTabs.map(({ key: tabKey, label, icon }) => {
          const active = page === tabKey;
          return (
            <button
              key={tabKey}
              onClick={() => setPage(tabKey)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: navCompact ? "0 12px" : "0 22px",
                border: "none",
                background: active ? `rgba(125,168,217,0.10)` : "none",
                cursor: "pointer",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: active ? 700 : 500,
                fontSize: 13,
                color: active ? NW_BRAND.white : NW_BRAND.dimWhite,
                borderBottom: active
                  ? `2.5px solid ${NW_BRAND.mauve}`
                  : "2.5px solid transparent",
                borderTop: "2.5px solid transparent",
                transition: "all 0.2s",
                position: "relative",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.color =
                    "#FFFFFF";
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(125,168,217,0.06)";
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.color =
                    NW_BRAND.dimWhite;
                  (e.currentTarget as HTMLElement).style.background = "none";
                }
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  color: active ? NW_BRAND.mauve : "rgba(255,255,255,0.72)",
                  transition: "color 0.2s",
                  filter: active
                    ? `drop-shadow(0 0 6px ${NW_BRAND.mauve}88)`
                    : "none",
                }}
              >
                {icon}
              </span>
              {label}
              {active && (
                <span
                  style={{
                    position: "absolute",
                    bottom: -1,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: NW_BRAND.mauve,
                    boxShadow: `0 0 10px ${NW_BRAND.mauve}`,
                  }}
                />
              )}
            </button>
          );
        })}

        {/* ── Analyse dropdown ── */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "stretch",
          }}
          onMouseEnter={openDropdown}
          onMouseLeave={closeDropdown}
        >
          <button
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: navCompact ? "0 12px" : "0 22px",
              border: "none",
              background: isAnalysePage ? `rgba(125,168,217,0.10)` : "none",
              cursor: "pointer",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontWeight: isAnalysePage ? 700 : 500,
              fontSize: 13,
              color: isAnalysePage ? NW_BRAND.white : NW_BRAND.dimWhite,
              borderBottom: isAnalysePage
                ? `2.5px solid ${NW_BRAND.mauve}`
                : analyseOpen
                  ? `2.5px solid ${NW_BRAND.mauve}55`
                  : "2.5px solid transparent",
              borderTop: "2.5px solid transparent",
              transition: "all 0.2s",
              position: "relative",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontSize: 16,
                color: isAnalysePage
                  ? NW_BRAND.mauve
                  : "rgba(255,255,255,0.72)",
                transition: "color 0.2s",
                filter: isAnalysePage
                  ? `drop-shadow(0 0 6px ${NW_BRAND.mauve}88)`
                  : "none",
              }}
            >
              ◈
            </span>
            Analyse
            {/* ── Count badge ── */}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                background:
                  analyseOpen || isAnalysePage
                    ? NW_BRAND.mauve
                    : "rgba(125,168,217,0.28)",
                color:
                  analyseOpen || isAnalysePage
                    ? NW_BRAND.bgDeep
                    : "rgba(255,255,255,0.50)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 0,
                lineHeight: 1,
                transition: "all 0.2s",
                boxShadow:
                  analyseOpen || isAnalysePage
                    ? `0 0 8px ${NW_BRAND.mauve}88`
                    : "none",
                marginLeft: 2,
              }}
            >
              {visibleAnalyseItems.length}
            </span>
            <span
              style={{
                fontSize: 9,
                marginLeft: 2,
                color: isAnalysePage
                  ? NW_BRAND.mauve
                  : "rgba(255,255,255,0.72)",
                transition: "transform 0.2s, color 0.2s",
                display: "inline-block",
                transform: analyseOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              ▼
            </span>
            {isAnalysePage && (
              <span
                style={{
                  position: "absolute",
                  bottom: -1,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: NW_BRAND.mauve,
                  boxShadow: `0 0 10px ${NW_BRAND.mauve}`,
                }}
              />
            )}
          </button>

          {/* Analyse Dropdown menu */}
          {analyseOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                minWidth: 230,
                background: `linear-gradient(160deg, #1D5BA8 0%, #1D5BA8 100%)`,
                border: `1px solid ${NW_BRAND.mauveBorder}`,
                borderRadius: "0 0 12px 12px",
                boxShadow: `0 12px 40px rgba(15,23,42,0.55), 0 0 0 1px ${NW_BRAND.mauveBorder}`,
                overflow: "hidden",
                animation: "nwDropFade 0.18s ease-out",
                zIndex: 200,
                maxHeight: 162,
                overflowY: "auto",
              }}
              onMouseEnter={openDropdown}
              onMouseLeave={closeDropdown}
            >
              {visibleAnalyseItems.map((item, i) => {
                const active = page === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => {
                      setPage(item.key);
                      setAnalyseOpen(false);
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "11px 18px",
                      border: "none",
                      borderBottom:
                        i < visibleAnalyseItems.length - 1
                          ? `1px solid ${NW_BRAND.faintLine}`
                          : "none",
                      background: active
                        ? "rgba(125,168,217,0.14)"
                        : "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!active)
                        (e.currentTarget as HTMLElement).style.background =
                          "rgba(125,168,217,0.09)";
                    }}
                    onMouseLeave={(e) => {
                      if (!active)
                        (e.currentTarget as HTMLElement).style.background =
                          "transparent";
                    }}
                  >
                    <span
                      style={{
                        fontSize: 18,
                        color: active
                          ? NW_BRAND.mauve
                          : "rgba(255,255,255,0.72)",
                        filter: active
                          ? `drop-shadow(0 0 5px ${NW_BRAND.mauve}99)`
                          : "none",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </span>
                    <div>
                      <div
                        style={{
                          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                          fontWeight: active ? 700 : 600,
                          fontSize: 13,
                          color: active
                            ? NW_BRAND.white
                            : "rgba(255,255,255,0.72)",
                          lineHeight: 1.2,
                        }}
                      >
                        {item.label}
                      </div>
                    </div>
                    {active && (
                      <span
                        style={{
                          marginLeft: "auto",
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: NW_BRAND.mauve,
                          boxShadow: `0 0 8px ${NW_BRAND.mauve}`,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── IA dropdown ── */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "stretch",
          }}
          onMouseEnter={openIaDropdown}
          onMouseLeave={closeIaDropdown}
        >
          <button
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: navCompact ? "0 12px" : "0 22px",
              border: "none",
              background: isIaPage ? `rgba(125,168,217,0.10)` : "none",
              cursor: "pointer",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontWeight: isIaPage ? 700 : 500,
              fontSize: 13,
              color: isIaPage ? NW_BRAND.white : NW_BRAND.dimWhite,
              borderBottom: isIaPage
                ? `2.5px solid ${NW_BRAND.mauve}`
                : iaOpen
                  ? `2.5px solid ${NW_BRAND.mauve}55`
                  : "2.5px solid transparent",
              borderTop: "2.5px solid transparent",
              transition: "all 0.2s",
              position: "relative",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontSize: 16,
                color: isIaPage ? NW_BRAND.mauve : "rgba(255,255,255,0.72)",
                transition: "color 0.2s",
                filter: isIaPage
                  ? `drop-shadow(0 0 6px ${NW_BRAND.mauve}88)`
                  : "none",
              }}
            >
              ✦{"\uFE0E"}
            </span>
            IA
            {/* ── Count badge ── */}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                background:
                  iaOpen || isIaPage
                    ? NW_BRAND.mauve
                    : "rgba(125,168,217,0.28)",
                color:
                  iaOpen || isIaPage
                    ? NW_BRAND.bgDeep
                    : "rgba(255,255,255,0.50)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 0,
                lineHeight: 1,
                transition: "all 0.2s",
                boxShadow:
                  iaOpen || isIaPage ? `0 0 8px ${NW_BRAND.mauve}88` : "none",
                marginLeft: 2,
              }}
            >
              {IA_ITEMS.length}
            </span>
            <span
              style={{
                fontSize: 9,
                marginLeft: 2,
                color: isIaPage ? NW_BRAND.mauve : "rgba(255,255,255,0.72)",
                transition: "transform 0.2s, color 0.2s",
                display: "inline-block",
                transform: iaOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              ▼
            </span>
            {isIaPage && (
              <span
                style={{
                  position: "absolute",
                  bottom: -1,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: NW_BRAND.mauve,
                  boxShadow: `0 0 10px ${NW_BRAND.mauve}`,
                }}
              />
            )}
          </button>

          {/* IA Dropdown menu */}
          {iaOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                minWidth: 230,
                background: `linear-gradient(160deg, #1D5BA8 0%, #1D5BA8 100%)`,
                border: `1px solid ${NW_BRAND.mauveBorder}`,
                borderRadius: "0 0 12px 12px",
                boxShadow: `0 12px 40px rgba(15,23,42,0.55), 0 0 0 1px ${NW_BRAND.mauveBorder}`,
                overflow: "hidden",
                animation: "nwDropFade 0.18s ease-out",
                zIndex: 200,
                overflowY: "auto",
              }}
              onMouseEnter={openIaDropdown}
              onMouseLeave={closeIaDropdown}
            >
              {IA_ITEMS.map((item, i) => {
                const active = page === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => {
                      setPage(item.key);
                      setIaOpen(false);
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "11px 18px",
                      border: "none",
                      borderBottom:
                        i < IA_ITEMS.length - 1
                          ? `1px solid ${NW_BRAND.faintLine}`
                          : "none",
                      background: active
                        ? "rgba(125,168,217,0.14)"
                        : "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!active)
                        (e.currentTarget as HTMLElement).style.background =
                          "rgba(125,168,217,0.09)";
                    }}
                    onMouseLeave={(e) => {
                      if (!active)
                        (e.currentTarget as HTMLElement).style.background =
                          "transparent";
                    }}
                  >
                    <span
                      style={{
                        fontSize: 18,
                        color: active
                          ? NW_BRAND.mauve
                          : "rgba(255,255,255,0.72)",
                        filter: active
                          ? `drop-shadow(0 0 5px ${NW_BRAND.mauve}99)`
                          : "none",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </span>
                    <div>
                      <div
                        style={{
                          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                          fontWeight: active ? 700 : 600,
                          fontSize: 13,
                          color: active
                            ? NW_BRAND.white
                            : "rgba(255,255,255,0.72)",
                          lineHeight: 1.2,
                        }}
                      >
                        {item.label}
                      </div>
                    </div>
                    {active && (
                      <span
                        style={{
                          marginLeft: "auto",
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: NW_BRAND.mauve,
                          boxShadow: `0 0 8px ${NW_BRAND.mauve}`,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </nav>

      {navCollapsed && <div style={{ flex: 1 }} />}

      {/* ── Bouton menu (petits écrans) ── */}
      {navCollapsed && (
        <button
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Menu"
          aria-expanded={menuOpen}
          style={{
            alignSelf: "center",
            width: 40,
            height: 40,
            marginRight: 4,
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.25)",
            background: menuOpen ? "rgba(255,255,255,0.18)" : "transparent",
            color: NW_BRAND.white,
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          {menuOpen ? "✕" : "☰"}
        </button>
      )}

      {/* ── Right: status + settings ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: navCompact ? 10 : 14,
          padding: navCompact ? "0 12px" : "0 20px",
          borderLeft: `1px solid ${NW_BRAND.faintLine}`,
          flexShrink: 0,
        }}
      >
        {/* Live indicator */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#4DB3A5",
              boxShadow: "0 0 8px #4DB3A5",
              animation: "pulse-green 2s infinite",
            }}
          />
          {!navCompact && (
            <span
              style={{
                fontSize: 11,
                color: NW_BRAND.dimWhite,
                fontWeight: 700,
                letterSpacing: 1,
                whiteSpace: "nowrap",
              }}
            >
              SSAS LIVE
            </span>
          )}
        </div>

        {/* ── Settings button + dropdown ── */}
        <div
          style={{ position: "relative" }}
          onMouseEnter={openSettings}
          onMouseLeave={closeSettings}
        >
          <button
            title="Paramètres"
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: settingsOpen
                ? "rgba(125,168,217,0.30)"
                : NW_BRAND.mauveDim,
              border: `2px solid ${settingsOpen ? NW_BRAND.mauve : NW_BRAND.mauveBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
              cursor: "pointer",
              color: settingsOpen ? NW_BRAND.white : NW_BRAND.dimWhite,
              transition: "all 0.18s",
              overflow: "hidden",
              padding: 0,
              boxShadow: settingsOpen ? `0 0 14px ${NW_BRAND.mauve}55` : "none",
            }}
          >
            {getPhotoUrl() ? (
              <img
                src={getPhotoUrl()}
                alt="Profil"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            ) : (
              <span style={{ fontSize: 15 }}>⚙</span>
            )}
          </button>

          {/* ── Settings dropdown menu ── */}
          {settingsOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                minWidth: 200,
                background: `linear-gradient(160deg, #1D5BA8 0%, #1D5BA8 100%)`,
                border: `1px solid ${NW_BRAND.mauveBorder}`,
                borderRadius: 12,
                boxShadow: `0 12px 40px rgba(15,23,42,0.65), 0 0 0 1px ${NW_BRAND.mauveBorder}`,
                overflow: "hidden",
                animation: "nwDropFade 0.18s ease-out",
                zIndex: 300,
              }}
              onMouseEnter={openSettings}
              onMouseLeave={closeSettings}
            >
              {/* User info header */}
              <div
                style={{
                  padding: "14px 16px 10px",
                  borderBottom: `1px solid ${NW_BRAND.faintLine}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
                    border: `2px solid ${NW_BRAND.mauveBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    flexShrink: 0,
                    overflow: "hidden",
                    boxShadow: `0 0 12px ${NW_BRAND.mauveDim}`,
                  }}
                >
                  {getPhotoUrl() ? (
                    <img
                      src={getPhotoUrl()}
                      alt="Profil"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <span>👤</span>
                  )}
                </div>
                <div style={{ overflow: "hidden" }}>
                  <div
                    style={{
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                      fontWeight: 700,
                      fontSize: 12,
                      color: NW_BRAND.white,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {getUserName()}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: "rgba(125,168,217,0.6)",
                      marginTop: 1,
                    }}
                  >
                    Connecté
                  </div>
                </div>
              </div>

              {/* Profile item */}
              <button
                onClick={handleGoProfile}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  padding: "11px 16px",
                  border: "none",
                  borderBottom: `1px solid ${NW_BRAND.faintLine}`,
                  background:
                    page === "profile"
                      ? "rgba(125,168,217,0.14)"
                      : "transparent",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (page !== "profile")
                    (e.currentTarget as HTMLElement).style.background =
                      "rgba(125,168,217,0.09)";
                }}
                onMouseLeave={(e) => {
                  if (page !== "profile")
                    (e.currentTarget as HTMLElement).style.background =
                      "transparent";
                }}
              >
                <span
                  style={{
                    fontSize: 16,
                    color:
                      page === "profile"
                        ? NW_BRAND.mauve
                        : "rgba(255,255,255,0.72)",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    overflow: "hidden",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {getPhotoUrl() ? (
                    <img
                      src={getPhotoUrl()}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    "👤"
                  )}
                </span>
                <div>
                  <div
                    style={{
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                      fontWeight: 600,
                      fontSize: 13,
                      color:
                        page === "profile"
                          ? NW_BRAND.white
                          : "rgba(255,255,255,0.80)",
                      lineHeight: 1.2,
                    }}
                  >
                    Mon Profil
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "rgba(125,168,217,0.50)",
                      marginTop: 2,
                    }}
                  >
                    Voir mes informations
                  </div>
                </div>
                {page === "profile" && (
                  <span
                    style={{
                      marginLeft: "auto",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: NW_BRAND.mauve,
                      boxShadow: `0 0 8px ${NW_BRAND.mauve}`,
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>

              {/* Logout item */}
              <button
                onClick={handleLogout}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  padding: "11px 16px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(214,69,69,0.10)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "transparent";
                }}
              >
                <span
                  style={{
                    fontSize: 16,
                    color: "rgba(214,69,69,0.70)",
                  }}
                >
                  ⎋
                </span>
                <div>
                  <div
                    style={{
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                      fontWeight: 600,
                      fontSize: 13,
                      color: "rgba(214,69,69,0.90)",
                      lineHeight: 1.2,
                    }}
                  >
                    Déconnexion
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "rgba(214,69,69,0.45)",
                      marginTop: 2,
                    }}
                  >
                    Quitter la session
                  </div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 8px #4DB3A5; }
          50% { box-shadow: 0 0 14px #4DB3A5, 0 0 4px #4DB3A5; }
        }
        @keyframes nwDropFade {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        /* Scrollbar styling inside dropdown */
        header div[style*="overflowY"]::-webkit-scrollbar {
          width: 4px;
        }
        header div[style*="overflowY"]::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.04);
        }
        header div[style*="overflowY"]::-webkit-scrollbar-thumb {
          background: rgba(125,168,217,0.35);
          border-radius: 99px;
        }
      `}</style>

      {navCollapsed && menuOpen && (
        <MobileNav
          page={page}
          sections={[
            { title: "Navigation", items: visibleTopTabs },
            { title: "Analyses", items: visibleAnalyseItems },
            { title: "Intelligence artificielle", items: IA_ITEMS },
          ]}
          onSelect={goTo}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default AppBar;